import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

import { init as initApm } from '@elastic/apm-rum';
// @ts-ignore
import { afterFrame } from '@elastic/apm-rum-core';

import { environment } from '../../../environments/environment';

@Injectable()
export class ElasticApmService {
  private queryParamsToRemove = [
    'gclid',
    'fbclid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  private apm;

  constructor(private router: Router) {
    // API is exposed through this apm instance
    const elkAPM = environment.elkAPM;
    if (elkAPM) {
      console.log('Init APM (ELK}', elkAPM, environment.elkAPMDistributedTracingOrigins);
      this.apm = initApm({
        serviceName: 'sh-client',
        serviceVersion: environment.app_version,
        serverUrl: elkAPM,
        environment: environment.name,
        active: !!environment.elkAPM,
        distributedTracingOrigins: environment.elkAPMDistributedTracingOrigins
      });

      // TODO build prod context ? https://www.elastic.co/guide/en/apm/agent/rum-js/current/install-the-agent.html#production-build ?

      this.observe();
    }
  }

  public observe() {
    let transaction: any;
    this.router.events.subscribe(event => {
      if (this.apm) {
        if (event instanceof NavigationStart) {
          const name = (event as NavigationStart).url;
          transaction = this.apm.startTransaction(this.sanitizeURLForAPMTransaction(name), 'route-change', {
            managed: true,
            canReuse: true
          });
        } else if (event instanceof NavigationError && transaction) {
          transaction.detectFinish();
        } else if (event instanceof NavigationEnd) {
          if (!transaction) {
            return;
          }

          /**
           * The below logic must be placed in NavigationEnd since
           * the we depend on the current route state to get the path
           *
           * Even If there are any redirects, the router state path
           * will be matched with the correct url on navigation end
           *
           * Traverse the activated route tree to figure out the nested
           * route path
           */
          const route = this.router.routerState.root.firstChild;

          if (route) {
            let child: ActivatedRoute | null = route;
            let path = child.routeConfig?.path ? child.routeConfig?.path : '';

            if (!path.startsWith('/')) {
              path = '/' + path;
            }

            while (child) {
              child = child.firstChild;
              if (child && child.routeConfig) {
                let currentPath = child.routeConfig.path;

                if (currentPath) {
                  if (!currentPath.startsWith('/') && !path.endsWith('/')) {
                    currentPath = '/' + currentPath;
                  }
                  path += currentPath;
                }
              }
            }

            transaction.name = this.sanitizeURLForAPMTransaction(path);
          }

          afterFrame(() => {
            transaction.detectFinish();
          });
        }
      }
    });
  }

  /**
   * Sanitize URL for transaction name:
   * - remove 'marketing' query parameters (utm parameters, fb click id, ...)
   */
  private sanitizeURLForAPMTransaction(url: string): string {
    for (const param of this.queryParamsToRemove) {
      url = this.removeURLParameter(url, param);
    }
    return url;
  }

  /**
   * Remove single URL parameter from URL. URL must be in full format (https|http://domain...)
   */
  private removeURLParameter(url: string, parameter: string) {
    const urlParts = url.split('?');
    if (urlParts.length >= 2) {
      const prefix = encodeURIComponent(parameter) + '=';
      const pars = urlParts[1].split(/[&;]/g);

      for (let i = pars.length - 1; i >= 0; i--) {
        if (pars[i].lastIndexOf(prefix, 0) !== -1) {
          pars.splice(i, 1);
        }
      }

      return urlParts[0] + (pars.length > 0 ? '?' + pars.join('&') : '');
    }
    return url;
  }
}
